import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Card, DatePicker, Space, Table, Statistic, Row, Col, Alert, theme, Divider, Spin } from 'antd'
import { FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { apiService, User } from '@/lib/api'
import DashboardLayout from '@/components/DashboardLayout'
import dayjs from 'dayjs'

const { Title, Text } = Typography

interface Account { id: number; account_code: string; account_name: string; balance: number }
interface BalanceSheetData {
  as_of_date: string; fiscal_year: number
  assets: { accounts: Account[]; total: number }
  liabilities: { accounts: Account[]; total: number }
  equity: { accounts: Account[]; equity_accounts_total: number; retained_earnings: number; current_year_net_income: number; total: number }
  total_liabilities_and_equity: number; balanced: boolean
}

export default function BalanceSheetPage() {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [user, setUser] = useState<User | null>(null)
  const [asOfDate, setAsOfDate] = useState<string>(dayjs().format('YYYY-MM-DD'))

  useEffect(() => { const storedUser = localStorage.getItem('user'); if (storedUser) setUser(JSON.parse(storedUser)) }, [])

  const handleLogout = () => { localStorage.removeItem('authToken'); localStorage.removeItem('user'); navigate('/') }

  const { data: balanceSheetResponse, isLoading, error } = useQuery({
    queryKey: ['balance-sheet', asOfDate],
    queryFn: async () => {
      const response = await apiService.getBalanceSheet(asOfDate)
      if (response.success && response.data) return response
      throw new Error(response.message || 'Failed to load balance sheet')
    }
  })

  const balanceSheet = balanceSheetResponse?.data as BalanceSheetData | undefined
  const accountColumns = [
    { title: 'Code', dataIndex: 'account_code', key: 'account_code', width: 120, render: (text: string) => <Text strong>{text}</Text> },
    { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
    { title: 'Balance', dataIndex: 'balance', key: 'balance', align: 'right' as const, width: 180, render: (balance: number) => <Text strong style={{ color: balance < 0 ? token.colorError : token.colorText }}>MYR {Number(balance).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text> }
  ]

  return (
    <DashboardLayout user={user} onLogout={handleLogout} title="Balance Sheet" subtitle="View your company's financial position" titleIcon={<FileTextOutlined />}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Row gutter={16} align="middle">
            <Col><Text strong>As of Date:</Text></Col>
            <Col><DatePicker value={dayjs(asOfDate)} onChange={(date) => { if (date) setAsOfDate(date.format('YYYY-MM-DD')) }} format="DD MMM YYYY" suffixIcon={<CalendarOutlined />} /></Col>
            {balanceSheet && <Col flex={1} style={{ textAlign: 'right' }}><Text type="secondary">Fiscal Year: {balanceSheet.fiscal_year}</Text></Col>}
          </Row>
        </Card>

        {isLoading && <Card><div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /><div style={{ marginTop: 16 }}><Text type="secondary">Loading balance sheet...</Text></div></div></Card>}
        {error && <Alert message="Error Loading Balance Sheet" description={error instanceof Error ? error.message : 'An error occurred'} type="error" showIcon />}

        {balanceSheet && !isLoading && (
          <>
            <Alert message={balanceSheet.balanced ? 'Balance Sheet is Balanced' : 'Balance Sheet is NOT Balanced'} description={balanceSheet.balanced ? 'Assets = Liabilities + Equity' : 'Warning: There is a discrepancy in the accounting equation. Please review your transactions.'} type={balanceSheet.balanced ? 'success' : 'error'} showIcon icon={balanceSheet.balanced ? <CheckCircleOutlined /> : <CloseCircleOutlined />} />

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}><Card><Statistic title="Total Assets" value={balanceSheet.assets.total} precision={2} prefix="MYR" valueStyle={{ color: token.colorPrimary }} /></Card></Col>
              <Col xs={24} sm={8}><Card><Statistic title="Total Liabilities" value={balanceSheet.liabilities.total} precision={2} prefix="MYR" valueStyle={{ color: token.colorWarning }} /></Card></Col>
              <Col xs={24} sm={8}><Card><Statistic title="Total Equity" value={balanceSheet.equity.total} precision={2} prefix="MYR" valueStyle={{ color: token.colorSuccess }} /></Card></Col>
            </Row>

            <Card title={<Text strong style={{ fontSize: 16 }}>ASSETS</Text>}>
              <Table columns={accountColumns} dataSource={balanceSheet.assets.accounts} rowKey="id" pagination={false} size="small" scroll={{ x: 500 }} summary={() => (
                <Table.Summary><Table.Summary.Row style={{ backgroundColor: token.colorPrimaryBg }}><Table.Summary.Cell index={0} colSpan={2}><Text strong style={{ fontSize: 16 }}>Total Assets</Text></Table.Summary.Cell><Table.Summary.Cell index={2} align="right"><Text strong style={{ fontSize: 16, color: token.colorPrimary }}>MYR {Number(balanceSheet.assets.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Table.Summary.Cell></Table.Summary.Row></Table.Summary>
              )} />
            </Card>

            <Card title={<Text strong style={{ fontSize: 16 }}>LIABILITIES</Text>}>
              <Table columns={accountColumns} dataSource={balanceSheet.liabilities.accounts} rowKey="id" pagination={false} size="small" summary={() => (
                <Table.Summary><Table.Summary.Row style={{ backgroundColor: token.colorWarningBg }}><Table.Summary.Cell index={0} colSpan={2}><Text strong style={{ fontSize: 16 }}>Total Liabilities</Text></Table.Summary.Cell><Table.Summary.Cell index={2} align="right"><Text strong style={{ fontSize: 16, color: token.colorWarning }}>MYR {Number(balanceSheet.liabilities.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Table.Summary.Cell></Table.Summary.Row></Table.Summary>
              )} />
            </Card>

            <Card title={<Text strong style={{ fontSize: 16 }}>EQUITY</Text>}>
              <Table columns={accountColumns} dataSource={balanceSheet.equity.accounts} rowKey="id" pagination={false} size="small" />
              <Divider style={{ margin: '16px 0' }} />
              <div style={{ paddingLeft: 16, paddingRight: 16 }}>
                <Row justify="space-between" style={{ padding: '8px 0' }}><Col><Text>Retained Earnings</Text></Col><Col><Text strong>MYR {Number(balanceSheet.equity.retained_earnings).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row>
                <Row justify="space-between" style={{ padding: '8px 0' }}><Col><Text>Current Year Net Income</Text></Col><Col><Text strong style={{ color: balanceSheet.equity.current_year_net_income >= 0 ? token.colorSuccess : token.colorError }}>MYR {Number(balanceSheet.equity.current_year_net_income).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row>
              </div>
              <Divider style={{ margin: '16px 0' }} />
              <div style={{ backgroundColor: token.colorSuccessBg, padding: 16, borderRadius: 8 }}><Row justify="space-between"><Col><Text strong style={{ fontSize: 16 }}>Total Equity</Text></Col><Col><Text strong style={{ fontSize: 16, color: token.colorSuccess }}>MYR {Number(balanceSheet.equity.total).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Col></Row></div>
            </Card>

            <Card>
              <div style={{ textAlign: 'center' }}>
                <Title level={5}>Accounting Equation</Title>
                <Row gutter={16} justify="center" align="middle">
                  <Col><Statistic title="Assets" value={balanceSheet.assets.total} precision={2} prefix="MYR" /></Col>
                  <Col><Title level={3} style={{ margin: '0 16px' }}>=</Title></Col>
                  <Col><Statistic title="Liabilities" value={balanceSheet.liabilities.total} precision={2} prefix="MYR" /></Col>
                  <Col><Title level={3} style={{ margin: '0 16px' }}>+</Title></Col>
                  <Col><Statistic title="Equity" value={balanceSheet.equity.total} precision={2} prefix="MYR" /></Col>
                </Row>
                <div style={{ marginTop: 16 }}>{balanceSheet.balanced ? <Alert message="Balanced" type="success" showIcon icon={<CheckCircleOutlined />} style={{ display: 'inline-block' }} /> : <Alert message="Not Balanced" type="error" showIcon icon={<CloseCircleOutlined />} style={{ display: 'inline-block' }} />}</div>
              </div>
            </Card>
          </>
        )}
      </Space>
    </DashboardLayout>
  )
}
